/**
 * A célula da coluna de fórmula, e o editor da expressão.
 *
 * ## Por que o editor mora na CÉLULA e não no menu da coluna
 *
 * O lugar "natural" seria o menu do cabeçalho, ao lado de *Number Format*. Ele não serve:
 * aquele submenu é **escrito à mão** em `view-presets/table/pc/header/database-header-column.ts`
 * com `hide: () => this.column.type$.value !== 'number'`, e repetido em mais dois arquivos
 * (`pc-virtual` e `mobile`). Não há ponto de extensão para um tipo de coluna contribuir com
 * configuração ali — abrir um custaria patch em três arquivos do upstream, e a Regra 8.15 diz
 * o que isso cobra na hora de atualizar.
 *
 * O `cellRenderer` **é** ponto de extensão, e a fórmula é a única coluna cuja configuração
 * interessa exatamente a quem está olhando uma célula dela. É também o que o Notion faz: o
 * clique na célula abre o editor da fórmula da coluna.
 *
 * ⚠️ **A consequência precisa estar visível na tela:** editar aqui muda a coluna INTEIRA, não
 * aquela linha. O painel diz isso com todas as letras — senão o usuário edita esperando mudar
 * um lançamento e muda 420.
 */

import { Popover, uniReactRoot } from '@affine/component';
import {
  type CellRenderProps,
  createIcon,
  type DataViewCellLifeCycle,
} from '@blocksuite/affine/blocks/database';
import {
  forwardRef,
  type ForwardRefRenderFunction,
  type ReactNode,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import { useSignalValue } from '../../../../modules/doc-info/utils';
import { analisar, calcular, FORMATOS, type FormatoDeSaida } from './avaliador';
import { formulaPropertyModelConfig } from './define';
import {
  dadosPadraoDaFormula, ehErroDeFormula, problemasDaConfiguracao, textoDaCelula,
  tipoLogicoDoFormato, type FormulaPropertyData, type FormulaRawValue,
} from './modelo';
import * as estilos from './style.css';

const ROTULO_DO_FORMATO: Record<string, string> = {
  numero: 'Número',
  moeda: 'Moeda (R$)',
  percentual: 'Percentual',
  data: 'Data',
  texto: 'Texto',
  booleano: 'Sim / Não',
};

type Props = CellRenderProps<FormulaPropertyData, FormulaRawValue, unknown>;

// ── a célula ──────────────────────────────────────────────────────────────────────────────

const CelulaDeFormula: ForwardRefRenderFunction<DataViewCellLifeCycle, Props> = (
  props,
  ref
): ReactNode => {
  /*
   * ⚠️ **TODOS os hooks ficam aqui em cima, antes de qualquer `return`.**
   *
   * A primeira versão deste arquivo lia `props.cell.property.name$` com `useSignalValue`
   * DENTRO do JSX do popover — depois do `if (!editando) return conteudo`. React conta
   * hooks por posição: a célula não-editando chamaria 4 e a editando 5, e o React derruba a
   * árvore com *"Rendered more hooks than during the previous render"*.
   *
   * O modo de falhar merece registro: **a célula funciona** enquanto ninguém a edita. O erro
   * só aparece no clique — e derruba a tabela inteira, não a célula.
   */
  const valor = useSignalValue(props.cell.value$);
  const dados = useSignalValue(props.cell.property.data$) ?? dadosPadraoDaFormula();
  const editando = useSignalValue(props.isEditing$);
  const somenteLeitura = useSignalValue(props.cell.view.readonly$);
  const nomeDaColuna = useSignalValue(props.cell.property.name$);
  const colunas = useSignalValue(props.cell.view.propertiesRaw$);

  useImperativeHandle(
    ref,
    () => ({
      // Entrar em edição abre o editor da EXPRESSÃO. Em documento somente-leitura não há o
      // que editar, e recusar aqui evita um popover que não escreve nada.
      beforeEnterEditMode: () => !somenteLeitura,
      beforeExitEditingMode: () => {},
      afterEnterEditingMode: () => {},
      focusCell: () => true,
      blurCell: () => true,
      forceUpdate: () => {},
    }),
    [somenteLeitura]
  );

  const conteudo = <ConteudoDaCelula valor={valor ?? null} dados={dados} />;

  if (!editando) return conteudo;

  return (
    <Popover
      open
      onOpenChange={aberto => {
        if (!aberto) props.selectCurrentCell(false);
      }}
      content={
        <EditorDaFormula
          dados={dados}
          nomeDaColuna={nomeDaColuna}
          colunas={colunasDisponiveis(colunas, props.cell.propertyId)}
          aoSalvar={mudancas => props.cell.property.dataUpdate(() => mudancas)}
        />
      }
    >
      <div style={{ width: '100%', height: '100%' }}>{conteudo}</div>
    </Popover>
  );
};

function ConteudoDaCelula({
  valor,
  dados,
}: {
  valor: FormulaRawValue;
  dados: FormulaPropertyData;
}) {
  if (ehErroDeFormula(valor)) {
    return (
      <div className={`${estilos.celula} ${estilos.erro}`} title={valor.mensagem}>
        {valor.rotulo}
      </div>
    );
  }

  const texto = textoDaCelula(valor, dados);
  if (!texto) {
    // ⚠️ Vazio é vazio — não "0", não "—". Um traço num relatório financeiro é lido como
    // "conferido e não se aplica"; branco é lido como "falta preencher", que é o que é.
    return <div className={`${estilos.celula} ${estilos.vazia}`} />;
  }

  const alinhaDireita = tipoLogicoDoFormato(dados.formato) === 'number';
  return (
    <div className={`${estilos.celula} ${alinhaDireita ? estilos.numerico : ''}`}>
      {texto}
    </div>
  );
}

/**
 * As colunas que a fórmula pode referenciar.
 *
 * ⚠️ `propertiesRaw$` e **não** `properties$`: o segundo já vem filtrado pela visão, então
 * esconder uma coluna faria ela sumir do editor — e a fórmula que a usa continuaria válida,
 * porque o dado gravado é o `propertyId`. O usuário veria uma expressão referenciando algo
 * que a lista não oferece, sem entender por quê.
 *
 * A própria coluna sai da lista: `prop("ela mesma")` é o ciclo mais curto que existe, e é
 * melhor não oferecer do que oferecer e recusar depois.
 */
function colunasDisponiveis(
  todas: readonly { id: string; name$: { value: string } }[],
  propriaColuna: string
): { id: string; nome: string }[] {
  return todas
    .filter(p => p.id !== propriaColuna)
    .map(p => ({ id: p.id, nome: p.name$.value }));
}

// ── o editor ──────────────────────────────────────────────────────────────────────────────

function EditorDaFormula({
  dados,
  nomeDaColuna,
  colunas,
  aoSalvar,
}: {
  dados: FormulaPropertyData;
  nomeDaColuna: string;
  colunas: { id: string; nome: string }[];
  aoSalvar: (mudancas: Partial<FormulaPropertyData>) => void;
}) {
  const [expressao, setExpressao] = useState(dados.expressao ?? '');
  const [formato, setFormato] = useState<string>(dados.formato ?? 'numero');

  const catalogo = useMemo(
    () => ({
      idPorNome: (nome: string) => colunas.find(c => c.nome === nome)?.id,
      nomes: () => colunas.map(c => c.nome),
    }),
    [colunas]
  );

  /**
   * A validação ao vivo (T2.5).
   *
   * Só `analisar` — **não** `avaliar`. Avaliar exigiria uma linha, e não há "a linha" enquanto
   * se digita; pior, avaliar contra uma linha qualquer mostraria um erro de dado (divisão por
   * zero naquela linha) como se fosse erro de expressão. O que se valida aqui é a EXPRESSÃO:
   * sintaxe, nomes de coluna, funções e aridade — tudo o que o parser já resolve.
   */
  const diagnostico = useMemo(() => {
    const texto = expressao.trim();
    if (!texto) return { tipo: 'neutro' as const, texto: 'Escreva a expressão.' };
    try {
      analisar(texto, catalogo);
      return { tipo: 'ok' as const, texto: 'Expressão válida.' };
    } catch (e) {
      return {
        tipo: 'erro' as const,
        texto: e instanceof Error ? e.message : String(e),
      };
    }
  }, [expressao, catalogo]);

  /** A prévia usa valores de mentira, e diz isso — senão vira número que alguém copia. */
  const previa = useMemo(() => {
    if (diagnostico.tipo !== 'ok') return null;
    const r = calcular(
      expressao,
      catalogo,
      {
        agora: Date.now(),
        locale: dados.locale || 'pt-BR',
        propertyIdAtual: '__previa__',
        valorDaCelula: () => 100,
        formulaDaColuna: () => null,
      },
      formato as FormatoDeSaida,
      { locale: dados.locale, moeda: dados.moeda, casas: dados.casas }
    );
    return r.ok ? r.texto : null;
  }, [diagnostico.tipo, expressao, catalogo, formato, dados]);

  const inserirColuna = useCallback((nome: string) => {
    setExpressao(anterior => `${anterior}${anterior ? ' ' : ''}prop("${nome}")`);
  }, []);

  const salvar = useCallback(() => {
    const mudancas = { expressao: expressao.trim(), formato };
    if (problemasDaConfiguracao(mudancas).length) return;
    aoSalvar(mudancas);
  }, [expressao, formato, aoSalvar]);

  return (
    <div className={estilos.editor}>
      <div className={estilos.ajuda}>
        Editando a fórmula da coluna <strong>{nomeDaColuna}</strong> — vale para{' '}
        <strong>todas as linhas</strong>, não só esta.
      </div>

      <div className={estilos.rotulo}>Expressão</div>
      <textarea
        className={`${estilos.campoDaExpressao} ${
          diagnostico.tipo === 'erro' ? estilos.campoComErro : ''
        }`}
        value={expressao}
        spellCheck={false}
        onChange={e => setExpressao(e.target.value)}
        onBlur={salvar}
        placeholder={'prop("Receita") - prop("Despesa")'}
      />

      <div
        className={`${estilos.mensagem} ${
          diagnostico.tipo === 'erro'
            ? estilos.mensagemErro
            : diagnostico.tipo === 'ok'
              ? estilos.mensagemOk
              : ''
        }`}
      >
        {diagnostico.texto}
      </div>

      {colunas.length > 0 && (
        <>
          <div className={estilos.rotulo}>Colunas disponíveis</div>
          <div className={estilos.listaDeColunas}>
            {colunas.map(c => (
              <button
                key={c.id}
                type="button"
                className={estilos.chipDeColuna}
                title={`Inserir prop("${c.nome}")`}
                onClick={() => inserirColuna(c.nome)}
              >
                {c.nome}
              </button>
            ))}
          </div>
        </>
      )}

      <div className={estilos.linha}>
        <div className={estilos.rotulo}>Formato</div>
        <select
          className={estilos.seletor}
          value={formato}
          onChange={e => {
            setFormato(e.target.value);
            aoSalvar({ expressao: expressao.trim(), formato: e.target.value });
          }}
        >
          {FORMATOS.map(f => (
            <option key={f} value={f}>
              {ROTULO_DO_FORMATO[f] ?? f}
            </option>
          ))}
        </select>
      </div>

      {previa !== null && (
        <div className={estilos.previa} title="Prévia com todas as colunas valendo 100">
          Prévia (colunas = 100): <strong>{previa}</strong>
        </div>
      )}

      <div className={estilos.ajuda}>
        {'Aritmética + − * / ( ) · prop("Coluna") · if(cond, a, b) · '}
        {'and/or/not · round/abs/min/max · dateBetween(a, b, "days") · '}
        {'month/year/today · concat/format · isEmpty/coalesce'}
      </div>
    </div>
  );
}

const Celula = forwardRef(CelulaDeFormula);

export const formulaPropertyConfig = formulaPropertyModelConfig.createPropertyMeta({
  icon: createIcon('DatabaseKanbanViewIcon'),
  cellRenderer: {
    view: uniReactRoot.createUniComponent(Celula),
  },
});
